import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Wallet, Plus, Mic, Sparkles, ArrowDownRight, ArrowUpRight,
  Repeat, Trash2, Pencil, Calendar as CalIcon, Receipt, X,
} from "lucide-react";

const usd = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "$0.00";
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const todayMonth = () => new Date().toISOString().slice(0, 7);

export default function Budget() {
  const [month, setMonth] = useState(todayMonth());
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [cats, setCats] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [loading, setLoading] = useState(true);

  // Quick add
  const [quickDesc, setQuickDesc] = useState("");
  const [quickAmount, setQuickAmount] = useState("");
  const [quickKind, setQuickKind] = useState("expense"); // expense | income
  const [adding, setAdding] = useState(false);
  const [aiHint, setAiHint] = useState(null); // { name, color, category_id }

  // Voice
  const [voiceListening, setVoiceListening] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState("");

  // Recurring dialog
  const [recOpen, setRecOpen] = useState(false);
  const [recForm, setRecForm] = useState({
    name: "", amount: "", type: "bill",
    frequency: "monthly", day_of_month: 1, anchor_date: "", category_id: "",
  });
  const [recSaving, setRecSaving] = useState(false);
  const [recEditing, setRecEditing] = useState(null);

  const refresh = async () => {
    try {
      const [s, t, c, r] = await Promise.all([
        axios.get(`${API}/budget/summary?month=${month}`, { withCredentials: true }),
        axios.get(`${API}/budget/transactions?month=${month}`, { withCredentials: true }),
        axios.get(`${API}/budget/categories`, { withCredentials: true }),
        axios.get(`${API}/budget/recurring`, { withCredentials: true }),
      ]);
      setSummary(s.data);
      setTxns(t.data || []);
      setCats(c.data || []);
      setRecurring(r.data || []);
      setLoading(false);
    } catch (err) {
      toast.error("Couldn't load budget");
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [month]);

  // Materialize on first load (idempotent)
  useEffect(() => {
    axios.post(`${API}/budget/recurring/materialize?month=${month}`, {}, { withCredentials: true })
      .then(() => refresh())
      .catch(() => { /* non-fatal */ });
    // eslint-disable-next-line
  }, [month]);

  // Live AI category preview as the user types a description
  useEffect(() => {
    if (quickKind !== "expense" || quickDesc.trim().length < 3) {
      setAiHint(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${API}/budget/categorize`,
          { description: quickDesc.trim(), amount: parseFloat(quickAmount) || null },
          { withCredentials: true },
        );
        if (res.data?.category_id) {
          setAiHint({
            category_id: res.data.category_id,
            name: res.data.category_name,
            color: res.data.color,
          });
        } else {
          setAiHint(null);
        }
      } catch (_) { /* ignore */ }
    }, 500);
    return () => clearTimeout(handle);
  }, [quickDesc, quickKind, quickAmount]);

  const addQuick = async () => {
    const amt = parseFloat(quickAmount);
    if (!quickDesc.trim() || !amt || amt <= 0) {
      toast.error("Add a description and amount");
      return;
    }
    setAdding(true);
    try {
      const signed = quickKind === "income" ? Math.abs(amt) : -Math.abs(amt);
      await axios.post(`${API}/budget/transactions`, {
        amount: signed,
        description: quickDesc.trim(),
        category_id: quickKind === "expense" ? aiHint?.category_id : null,
        source: "manual",
      }, { withCredentials: true });
      toast.success(quickKind === "income" ? "Income added" : "Expense logged");
      setQuickDesc(""); setQuickAmount(""); setAiHint(null);
      refresh();
    } catch (err) {
      toast.error("Couldn't add");
    } finally { setAdding(false); }
  };

  // ----- Voice (browser SpeechRecognition) -----
  const startVoice = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Voice input isn't supported on this browser. Try Chrome/Safari.");
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = "en-US";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    setVoiceListening(true);
    setVoiceTranscript("");
    rec.onresult = async (e) => {
      const transcript = e.results[0][0].transcript;
      setVoiceTranscript(transcript);
      try {
        const res = await axios.post(`${API}/budget/voice`,
          { transcript }, { withCredentials: true });
        toast.success(
          `Logged: ${usd(res.data.transaction.amount)}` +
          (res.data.category_name ? ` · ${res.data.category_name}` : "") +
          (res.data.ai_categorized ? " (AI)" : ""),
        );
        refresh();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't parse that");
      } finally { setVoiceListening(false); }
    };
    rec.onerror = () => { setVoiceListening(false); toast.error("Voice failed"); };
    rec.onend = () => setVoiceListening(false);
    rec.start();
  };

  const deleteTxn = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      await axios.delete(`${API}/budget/transactions/${id}`, { withCredentials: true });
      toast.success("Deleted");
      refresh();
    } catch (_) { toast.error("Couldn't delete"); }
  };

  // ----- Recurring CRUD -----
  const openRecDialog = (rec = null) => {
    setRecEditing(rec);
    setRecForm({
      name: rec?.name || "",
      amount: rec ? String(Math.abs(rec.amount)) : "",
      type: rec?.type || "bill",
      frequency: rec?.frequency || "monthly",
      day_of_month: rec?.day_of_month ?? 1,
      anchor_date: rec?.anchor_date || "",
      category_id: rec?.category_id || "",
    });
    setRecOpen(true);
  };

  const saveRec = async () => {
    const amt = parseFloat(recForm.amount);
    if (!recForm.name.trim() || !amt) {
      toast.error("Name and amount required");
      return;
    }
    setRecSaving(true);
    try {
      const payload = {
        name: recForm.name.trim(),
        amount: amt,
        type: recForm.type,
        frequency: recForm.frequency,
        day_of_month: ["monthly", "semimonthly"].includes(recForm.frequency)
          ? parseInt(recForm.day_of_month, 10) || 1 : null,
        anchor_date: ["weekly", "biweekly"].includes(recForm.frequency)
          ? recForm.anchor_date || null : null,
        category_id: recForm.category_id || null,
        active: true,
      };
      if (recEditing) {
        await axios.patch(`${API}/budget/recurring/${recEditing.recurring_id}`,
          payload, { withCredentials: true });
        toast.success("Updated");
      } else {
        await axios.post(`${API}/budget/recurring`, payload, { withCredentials: true });
        toast.success("Added");
      }
      // Re-materialize the current month so the new bill/income appears
      await axios.post(`${API}/budget/recurring/materialize?month=${month}`, {},
        { withCredentials: true });
      setRecOpen(false);
      refresh();
    } catch (err) {
      toast.error("Couldn't save");
    } finally { setRecSaving(false); }
  };

  const deleteRec = async (id) => {
    if (!window.confirm("Delete this recurring entry? Past auto-generated transactions stay.")) return;
    try {
      await axios.delete(`${API}/budget/recurring/${id}`, { withCredentials: true });
      toast.success("Deleted");
      refresh();
    } catch (_) { toast.error("Couldn't delete"); }
  };

  const catMap = useMemo(() => {
    const m = {};
    cats.forEach(c => { m[c.category_id] = c; });
    return m;
  }, [cats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const balance = summary?.balance ?? 0;
  const balanceTone = balance >= 0 ? "text-emerald-400" : "text-red-400";

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-8" data-testid="budget-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Wallet className="w-7 h-7 text-emerald-400" />
            Budget
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cash in, cash out — and what's left. Private to you.
          </p>
        </div>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value || todayMonth())}
          className="bg-zinc-900 border border-zinc-700 text-white text-sm rounded px-3 py-2"
          data-testid="budget-month-picker"
        />
      </div>

      {/* Big balance card */}
      <Card className="overflow-hidden">
        <CardContent className="p-6 sm:p-8">
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Left this month</div>
              <div className={`text-4xl sm:text-5xl font-bold ${balanceTone}`} data-testid="budget-balance">
                {usd(balance)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {summary?.days_remaining} days remaining
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Income</div>
              <div className="text-2xl font-semibold text-emerald-300 flex items-center gap-2">
                <ArrowUpRight className="w-5 h-5" />
                {usd(summary?.income)}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Spent</div>
              <div className="text-2xl font-semibold text-red-300 flex items-center gap-2">
                <ArrowDownRight className="w-5 h-5" />
                {usd(summary?.spent)}
              </div>
            </div>
          </div>

          {summary?.upcoming_bills_total > 0 && (
            <div className="mt-6 pt-6 border-t border-zinc-800 flex items-center justify-between gap-4">
              <div className="text-sm">
                <span className="text-muted-foreground">Upcoming bills this month: </span>
                <span className="font-semibold text-amber-300">{usd(summary.upcoming_bills_total)}</span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Left after bills: </span>
                <span className={`font-semibold ${summary.left_after_upcoming_bills >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                  {usd(summary.left_after_upcoming_bills)}
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick entry */}
      <Card>
        <CardContent className="p-4 sm:p-6 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold flex items-center gap-2">
              <Plus className="w-4 h-4 text-violet-400" />
              Quick entry
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={startVoice}
              disabled={voiceListening}
              data-testid="budget-voice-btn"
              className="text-emerald-300 border-emerald-500/40"
            >
              <Mic className={`w-4 h-4 mr-1 ${voiceListening ? "animate-pulse text-red-400" : ""}`} />
              {voiceListening ? "Listening…" : "Voice"}
            </Button>
          </div>
          {voiceTranscript && (
            <div className="text-xs text-zinc-400 italic">
              Heard: "{voiceTranscript}"
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_120px_auto] gap-2">
            <Input
              value={quickDesc}
              onChange={(e) => setQuickDesc(e.target.value)}
              placeholder={quickKind === "income" ? "What is this income? (e.g. side gig)" : "What did you buy?"}
              data-testid="budget-quick-desc"
              onKeyDown={(e) => { if (e.key === "Enter") addQuick(); }}
            />
            <Input
              type="number"
              step="0.01"
              min="0"
              value={quickAmount}
              onChange={(e) => setQuickAmount(e.target.value)}
              placeholder="$"
              data-testid="budget-quick-amount"
              onKeyDown={(e) => { if (e.key === "Enter") addQuick(); }}
            />
            <Select value={quickKind} onValueChange={setQuickKind}>
              <SelectTrigger data-testid="budget-quick-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="expense">Expense</SelectItem>
                <SelectItem value="income">Income</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={addQuick}
              disabled={adding}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="budget-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
          {aiHint && quickKind === "expense" && (
            <div className="text-xs flex items-center gap-2 text-zinc-400" data-testid="budget-ai-hint">
              <Sparkles className="w-3 h-3 text-violet-400" />
              AI suggests:
              <Badge style={{ backgroundColor: aiHint.color + "33", color: aiHint.color, borderColor: aiHint.color + "55" }}>
                {aiHint.name}
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Two columns: Spend by category + Recurring */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-4 sm:p-6 space-y-4">
            <div className="font-semibold flex items-center gap-2">
              <Receipt className="w-4 h-4 text-violet-400" />
              Spend by category
            </div>
            {(summary?.by_category?.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">No spending yet this month.</p>
            ) : (
              <div className="space-y-2">
                {summary.by_category.map((b) => {
                  const target = b.monthly_target;
                  const pct = target ? Math.min(100, Math.round(b.spent / target * 100)) : null;
                  return (
                    <div key={b.category_id} data-testid={`spend-row-${b.category_id}`}>
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: b.color }}
                          />
                          <span className="truncate">{b.name}</span>
                          <span className="text-xs text-muted-foreground">· {b.count}</span>
                        </div>
                        <div className="font-semibold">
                          {usd(b.spent)}
                          {target && <span className="text-xs text-muted-foreground"> / {usd(target)}</span>}
                        </div>
                      </div>
                      {target && (
                        <div className="mt-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${pct}%`,
                              backgroundColor: pct >= 100 ? "#EF4444" : b.color,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold flex items-center gap-2">
                <Repeat className="w-4 h-4 text-violet-400" />
                Recurring (paychecks + bills)
              </div>
              <Button
                size="sm"
                onClick={() => openRecDialog(null)}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="budget-add-recurring-btn"
              >
                <Plus className="w-4 h-4 mr-1" />Add
              </Button>
            </div>
            {recurring.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Add your paycheck and recurring bills so they auto-post each month.
              </p>
            ) : (
              <div className="divide-y divide-zinc-800 -mx-4 sm:-mx-6">
                {recurring.map((r) => (
                  <div
                    key={r.recurring_id}
                    className="px-4 sm:px-6 py-3 flex items-center gap-3 hover:bg-zinc-900/30"
                    data-testid={`recurring-row-${r.recurring_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{r.name}</span>
                        {r.type === "income" ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/40">
                            income
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/40">
                            bill
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {r.frequency}
                        {r.day_of_month && ` · day ${r.day_of_month}`}
                        {r.anchor_date && ` · from ${r.anchor_date}`}
                      </div>
                    </div>
                    <div className={`font-semibold whitespace-nowrap ${r.type === "income" ? "text-emerald-300" : "text-red-300"}`}>
                      {usd(Math.abs(r.amount))}
                    </div>
                    <button
                      onClick={() => openRecDialog(r)}
                      className="p-1.5 rounded text-muted-foreground hover:text-white hover:bg-zinc-800"
                      aria-label="Edit"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => deleteRec(r.recurring_id)}
                      className="p-1.5 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                      aria-label="Delete"
                      data-testid={`delete-recurring-${r.recurring_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent transactions */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 sm:px-6 py-3 border-b border-zinc-800 font-semibold flex items-center gap-2">
            <CalIcon className="w-4 h-4 text-violet-400" />
            Recent activity ({txns.length})
          </div>
          {txns.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">
              No transactions yet. Add one above or via voice.
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {txns.map((t) => {
                const cat = catMap[t.category_id];
                const negative = t.amount < 0;
                return (
                  <div
                    key={t.txn_id}
                    className="px-4 sm:px-6 py-3 flex items-center gap-3 hover:bg-zinc-900/30"
                    data-testid={`txn-row-${t.txn_id}`}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: cat?.color || "#64748b" }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium truncate">{t.description}</span>
                        {cat && (
                          <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                            {cat.name}
                          </span>
                        )}
                        {t.source === "voice" && (
                          <span className="text-[10px] text-emerald-400 inline-flex items-center gap-0.5">
                            <Mic className="w-2.5 h-2.5" />voice
                          </span>
                        )}
                        {t.source === "recurring" && (
                          <span className="text-[10px] text-violet-400 inline-flex items-center gap-0.5">
                            <Repeat className="w-2.5 h-2.5" />auto
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{t.date}</div>
                    </div>
                    <div className={`font-semibold whitespace-nowrap ${negative ? "text-red-300" : "text-emerald-300"}`}>
                      {usd(t.amount)}
                    </div>
                    <button
                      onClick={() => deleteTxn(t.txn_id)}
                      className="p-1.5 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                      aria-label="Delete"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recurring dialog */}
      <Dialog open={recOpen} onOpenChange={setRecOpen}>
        <DialogContent data-testid="recurring-dialog">
          <DialogHeader>
            <DialogTitle>{recEditing ? "Edit recurring" : "Add recurring"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => setRecForm({ ...recForm, type: "income" })}
                variant={recForm.type === "income" ? "default" : "outline"}
                className={recForm.type === "income" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                data-testid="rec-type-income"
              >
                <ArrowUpRight className="w-4 h-4 mr-1" />Income
              </Button>
              <Button
                onClick={() => setRecForm({ ...recForm, type: "bill" })}
                variant={recForm.type === "bill" ? "default" : "outline"}
                className={recForm.type === "bill" ? "bg-red-600 hover:bg-red-700" : ""}
                data-testid="rec-type-bill"
              >
                <ArrowDownRight className="w-4 h-4 mr-1" />Bill
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={recForm.name}
                onChange={(e) => setRecForm({ ...recForm, name: e.target.value })}
                placeholder={recForm.type === "income" ? "Paycheck, Side gig…" : "Rent, Netflix…"}
                data-testid="rec-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Amount</Label>
              <Input
                type="number"
                step="0.01"
                value={recForm.amount}
                onChange={(e) => setRecForm({ ...recForm, amount: e.target.value })}
                placeholder="0.00"
                data-testid="rec-amount"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select
                  value={recForm.frequency}
                  onValueChange={(v) => setRecForm({ ...recForm, frequency: v })}
                >
                  <SelectTrigger data-testid="rec-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="semimonthly">Semi-monthly (1st & 15th)</SelectItem>
                    <SelectItem value="biweekly">Biweekly</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {["monthly", "semimonthly"].includes(recForm.frequency) ? (
                <div className="space-y-2">
                  <Label>Day of month</Label>
                  <Input
                    type="number" min="1" max="31"
                    value={recForm.day_of_month}
                    onChange={(e) => setRecForm({ ...recForm, day_of_month: e.target.value })}
                    data-testid="rec-day-of-month"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>First date</Label>
                  <Input
                    type="date"
                    value={recForm.anchor_date}
                    onChange={(e) => setRecForm({ ...recForm, anchor_date: e.target.value })}
                    data-testid="rec-anchor-date"
                  />
                </div>
              )}
            </div>
            {recForm.type === "bill" && (
              <div className="space-y-2">
                <Label>Category (optional)</Label>
                <Select
                  value={recForm.category_id || "_none"}
                  onValueChange={(v) => setRecForm({ ...recForm, category_id: v === "_none" ? "" : v })}
                >
                  <SelectTrigger><SelectValue placeholder="Pick a category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— None —</SelectItem>
                    {cats.map(c => (
                      <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setRecOpen(false)} disabled={recSaving}>
                Cancel
              </Button>
              <Button
                onClick={saveRec}
                disabled={recSaving}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="save-recurring-btn"
              >
                {recSaving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
